import { Asset } from '../../../models/image/dto/asset';
import { BulkEditAssetsRequest } from '../../../models/image/requests/bulk-edit-assets-request';
import { MediaManagerAssetLibraryDto } from '../../../models/image/dto/media-manager-asset-library-dto';
import { MediaManagerFolderDto } from '../../../models/image/dto/media-manager-folder-dto';
import { MediaManagerFileSystem } from '../../../models/media-manager/media-manager-file-system';
import { exists } from '../../../functions/exists';
import { findAssetInLibrary, findFolderInLibrary } from '../mutations/asset-library-lookups';
import { preflightAssetUpdateValidation } from './asset-preflight';
import { preflightFolderUpdateValidation } from './folder-preflight';

/**
 * Verify a bulk-edit request end-to-end: reject duplicate ids in the request, then verify each folder and
 * asset update individually, then reject requests that select an ancestor folder together with any of its
 * descendants (assets or subfolders).
 */
export function preflightBulkEditValidation(
  req: BulkEditAssetsRequest,
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): string | null {
  const duplicateFolderErrors = getDuplicateFolderUpdateErrors(req.updatedAssetFolders);
  const duplicateAssetErrors = getDuplicateAssetUpdateErrors(req.updatedAssets);
  const duplicateErrors = [...duplicateFolderErrors, ...duplicateAssetErrors];
  if (duplicateErrors.length) {
    return duplicateErrors.join('. ');
  }
  const folderErrors = preflightBulkFolderUpdates(req.updatedAssetFolders, assetLibrary, filesystem);
  const assetErrors = preflightBulkAssetUpdates(req.updatedAssets, assetLibrary, filesystem);
  const overlapErrors = getBulkSelectionOverlapErrors(req, assetLibrary, filesystem);
  const errors: string[] = [];
  if (folderErrors?.length) {
    errors.push(...folderErrors);
  }
  if (assetErrors?.length) {
    errors.push(...assetErrors);
  }
  if (overlapErrors?.length) {
    errors.push(...overlapErrors);
  }
  return errors.length ? errors.join('. ') : null;
}

/** Reject requests that mention the same folder id more than once. */
export function getDuplicateFolderUpdateErrors(updatedFolders: MediaManagerFolderDto[]): string[] {
  const errors: string[] = [];
  const seenFolderIds = new Set<string>();
  for (const updatedFolder of updatedFolders) {
    if (!updatedFolder.id?.trim()) {
      errors.push('Folder ID is not valid');
      continue;
    }
    if (seenFolderIds.has(updatedFolder.id)) {
      errors.push(`Duplicate folder update for id '${updatedFolder.id}'`);
      continue;
    }
    seenFolderIds.add(updatedFolder.id);
  }
  return errors;
}

/** Reject requests that mention the same asset unique id more than once. */
export function getDuplicateAssetUpdateErrors(updatedAssets: Asset[]): string[] {
  const errors: string[] = [];
  const seenAssetIds = new Set<string>();
  for (const updatedAsset of updatedAssets) {
    const updatedAssetId = updatedAsset.getUniqueIdentifier();
    if (!updatedAssetId?.trim()) {
      errors.push('Asset ID is not valid');
      continue;
    }
    if (seenAssetIds.has(updatedAssetId)) {
      errors.push(`Duplicate asset update for id '${updatedAssetId}'`);
      continue;
    }
    seenAssetIds.add(updatedAssetId);
  }
  return errors;
}

/** Run the single-folder preflight for every folder in the request; missing library entries are also errors. */
export function preflightBulkFolderUpdates(
  updatedFolders: MediaManagerFolderDto[],
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): string[] {
  const errors: string[] = [];
  const currentFoldersById = new Map<string, MediaManagerFolderDto>(
    assetLibrary.assetFolders.map(folder => [folder.id, folder])
  );
  for (const updatedFolder of updatedFolders) {
    const currentFolder = currentFoldersById.get(updatedFolder.id);
    if (!currentFolder) {
      errors.push(`MediaManager folder '${updatedFolder.id}' could not be found in assetLibrary`);
      continue;
    }
    const preflightErrors = preflightFolderUpdateValidation(updatedFolder, currentFolder, filesystem, assetLibrary);
    if (preflightErrors) {
      errors.push(preflightErrors);
    }
  }
  return errors;
}

/** Run the single-asset preflight for every asset in the request; missing library entries are also errors. */
export function preflightBulkAssetUpdates(
  updatedAssets: Asset[],
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): string[] {
  const errors: string[] = [];
  const currentAssetsById = new Map<string, Asset>(
    assetLibrary.assets.map(asset => [asset.getUniqueIdentifier(), asset])
  );
  for (const updatedAsset of updatedAssets) {
    const currentAsset = currentAssetsById.get(updatedAsset.getUniqueIdentifier());
    if (!currentAsset) {
      errors.push(`MediaManager asset '${updatedAsset.getUniqueIdentifier()}' could not be found in assetLibrary`);
      continue;
    }
    const preflightErrors = preflightAssetUpdateValidation(updatedAsset, currentAsset, filesystem, assetLibrary);
    if (preflightErrors) {
      errors.push(preflightErrors);
    }
  }
  return errors;
}

/**
 * Reject requests that select a folder together with any asset or subfolder already inside it. Callers should
 * either pick the folder alone (moves the full subtree) or pick individual descendants without the ancestor.
 */
export function getBulkSelectionOverlapErrors(
  req: BulkEditAssetsRequest,
  assetLibrary: MediaManagerAssetLibraryDto,
  filesystem: MediaManagerFileSystem
): string[] {
  const errors: string[] = [];
  const selectedFolders = req.updatedAssetFolders ?? [];
  const selectedAssets = req.updatedAssets ?? [];
  const selectedFolderIds = new Set(
    selectedFolders.map(folder => folder.id).filter(folderId => exists(folderId?.trim()))
  );
  for (const selectedFolder of selectedFolders) {
    const ancestorFolderId = filesystem.folderHasAnyAncestorInSet(selectedFolder.id, selectedFolderIds);
    if (ancestorFolderId) {
      const selectedFolderLabel = getFolderValidationLabel(selectedFolder.id, assetLibrary);
      const ancestorFolderLabel = getFolderValidationLabel(ancestorFolderId, assetLibrary);
      errors.push(
        `Bulk move cannot include folder ${selectedFolderLabel} and` +
          ` its ancestor folder ${ancestorFolderLabel} in the same request`
      );
    }
  }
  for (const selectedAsset of selectedAssets) {
    const assetId = selectedAsset.getUniqueIdentifier();
    const ancestorFolderId = filesystem.assetHasAnyAncestorInSet(assetId, selectedFolderIds);
    if (ancestorFolderId) {
      const assetLabel = getAssetValidationLabel(assetId, assetLibrary);
      const ancestorFolderLabel = getFolderValidationLabel(ancestorFolderId, assetLibrary);
      errors.push(
        `Bulk move cannot include asset ${assetLabel} and` +
          ` its containing folder ${ancestorFolderLabel} in the same request`
      );
    }
  }
  if (errors.length) {
    errors.unshift(
      `For bulk moves, do not select a folder together with any asset or subfolder already contained within it`,
      `Select the folder alone to move its full subtree, or select individual descendants without the ancestor folder`
    );
  }
  return errors;
}

/** Human-readable folder label used in validation messages: display name + id when available, else the id. */
function getFolderValidationLabel(folderId: string, assetLibrary: MediaManagerAssetLibraryDto): string {
  const folder = findFolderInLibrary(folderId, assetLibrary);
  const folderName = folder?.name?.trim();
  return folderName ? `"${folderName}" (id: ${folderId})` : folderId;
}

/** Human-readable asset label used in validation messages: display/file name + id when available, else the id. */
function getAssetValidationLabel(assetId: string, assetLibrary: MediaManagerAssetLibraryDto): string {
  const asset = findAssetInLibrary(assetId, assetLibrary);
  const assetName = asset?.displayName?.trim() || asset?.fileName?.trim() || asset?.id?.trim();
  return assetName ? `"${assetName}" (id: ${assetId})` : assetId;
}
